import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Spin, Alert, Button, Space } from 'antd';
import ReactMarkdown from 'react-markdown';
import { RobotOutlined } from '@ant-design/icons';
import { useUser } from '../../contexts/UserContext';
import { useChat } from '../../contexts/ChatContext';
import { useChatUI } from '../../contexts/ChatUIContext';
import { ModelType } from '../../types/models';
import { ChatInput } from '../../components/ChatInput/ChatInput';
import { AgentReplyWaitIndicator } from '../../components/ChatUI/AgentReplyWaitIndicator';
import { parseMessageContent } from '../../utils/fieldMapping';
import {
  COMPANY_CREATE_INSTRUCTION,
  COMPANY_MENU_INSTRUCTION_SHORT,
} from '../../utils/companySelectionHelp';
import { captureLandingSourceKeyFromLocation, readPersistedLandingSourceKey } from '../../utils/landingSourceKey';
import {
  isRcProspectSourceKey,
  readRcProspectKickoff,
} from '../../content/resilienceCircle/landingModel';
import { CONTROL_BRIDGE_HOME_PATH } from '../../utils/controlBridgeRoutes';
import { KrybotService } from '../../services/api/krybotService';
import { isPlatformAdministrator } from '../../components/AppShell/appNavAccess';
import type { OnboardingFlags, User } from '../../contexts/UserContext';
import { ProspectorArtifactSnapshot } from './ProspectorArtifactSnapshot';
import {
  readCachedClaudeProjectRef,
  readCachedProspectorThreadId,
  writeCachedProspectorThreadId,
} from './prospectorThreadContinuity';

const { Title, Paragraph, Text } = Typography;

/**
 * Orlin 2026-09-22: Skip to Control Bridge only when auth+company are present
 * (page already gates those) and subscription/CB entitlement — or platform override.
 * No Discovery intake-complete gate.
 */
export function isProspectorSkipToControlBridgeAllowed(
  user: User | null | undefined,
  onboarding?: OnboardingFlags | null
): boolean {
  if (isPlatformAdministrator(user)) return true;
  const flags = onboarding ?? user?.onboarding;
  if (!flags) return false;
  if (flags.coreActive) return true;
  if (flags.foundationComplete) return true;
  if (flags.modelReady) return true;
  const tier = (flags.subscriptionTier || 'none').trim().toLowerCase();
  return Boolean(tier && tier !== 'none');
}

/**
 * Full-width prospector (intake) chat — not the Control Bridge.
 * Creates a Krybot thread with ui_role `prospector` (ISSUE-243).
 * Resumes the latest server-side prospector thread for user+company when present.
 */
export const ProspectorChatPage: React.FC = () => {
  const {
    user,
    isAuthenticated,
    isLoading: userLoading,
    selectedCompany,
    availableCompanies,
    loadUserCompanies,
    selectCompany,
    refreshUser,
  } = useUser();
  const { messages, sendMessage, createThread, selectThread, isLoadingMessages, isTyping } =
    useChat();
  const { model: chatUIModel } = useChatUI();
  const navigate = useNavigate();
  const threadStarted = useRef(false);
  const lastProspectorCompanyRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const rcKickoffStarted = useRef(false);

  useEffect(() => {
    captureLandingSourceKeyFromLocation(
      typeof window !== 'undefined' ? window.location.search : ''
    );
  }, []);

  // Keep the latest turn visible — default chat UX
  useEffect(() => {
    const end = messagesEndRef.current;
    const container = messagesContainerRef.current;
    if (!end) return;
    // Prefer scrolling the message pane; fall back to scrollIntoView
    if (container) {
      container.scrollTop = container.scrollHeight;
    } else {
      end.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, isLoadingMessages, isTyping]);

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    void (async () => {
      setCompaniesLoaded(false);
      try {
        await refreshUser();
      } catch {
        /* session may be invalid; loadUserCompanies still runs for partial state */
      }
      await loadUserCompanies();
      setCompaniesLoaded(true);
    })();
  }, [user?.id, isAuthenticated, refreshUser, loadUserCompanies]);

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    // Header selection wins; profile companyId is fallback only (multi-company switcher).
    const companyId = selectedCompany?.id || user.companyId;
    if (!companyId) return;
    // selectCompany needs availableCompanies populated (see UserContext)
    if (availableCompanies.length === 0) return;
    const known = availableCompanies.some((uc) => uc.company.id === companyId);
    if (!known) return;

    if (!selectedCompany?.id && user.companyId) {
      void selectCompany(user.companyId);
      return;
    }

    if (lastProspectorCompanyRef.current !== companyId) {
      threadStarted.current = false;
      lastProspectorCompanyRef.current = companyId;
    }

    if (threadStarted.current) return;
    threadStarted.current = true;

    void (async () => {
      try {
        setBootstrapError(null);

        // 1) Server resume (works across devices / browsers for this account).
        // Do not fall through to createThread if selectThread fails — that minted
        // blank Discovery sessions that then became the "latest" resume target.
        let resumeThreadId: string | null = null;
        try {
          const resumed = await KrybotService.resumeChatThread({
            userId: user.id,
            agentMode: 'prospector',
            companyId,
          });
          if (resumed.resumed && resumed.thread?.id) {
            resumeThreadId = resumed.thread.id;
          }
        } catch {
          /* try cache / create below */
        }

        if (resumeThreadId) {
          writeCachedProspectorThreadId(user.id, companyId, resumeThreadId);
          await selectThread(resumeThreadId, { uiRole: 'prospector' });
          setActiveThreadId(resumeThreadId);
          return;
        }

        // 2) Same-browser cache hint (migrates sessionStorage → localStorage)
        const cachedId = readCachedProspectorThreadId(user.id, companyId);
        if (cachedId) {
          try {
            await selectThread(cachedId, { uiRole: 'prospector' });
            writeCachedProspectorThreadId(user.id, companyId, cachedId);
            setActiveThreadId(cachedId);
            return;
          } catch {
            /* stale id — create below */
          }
        }

        // 3) Net-new Discovery session only when server + cache have nothing
        const model =
          chatUIModel?.type === ModelType.COMPANY && chatUIModel.artifactTreeId
            ? chatUIModel
            : { type: ModelType.COMPANY as const };
        const claudeProjectRef = readCachedClaudeProjectRef(user.id, companyId);
        const thread = await createThread(
          'Prospector session',
          undefined,
          model,
          'prospector',
          {
            companyId,
            ...(claudeProjectRef ? { claude_project_ref: claudeProjectRef } : {}),
          }
        );
        writeCachedProspectorThreadId(user.id, companyId, thread.id);
        await selectThread(thread.id, { uiRole: 'prospector' });
        setActiveThreadId(thread.id);
      } catch (e: unknown) {
        threadStarted.current = false;
        const msg = e instanceof Error ? e.message : 'Could not start prospector chat';
        setBootstrapError(msg);
      }
    })();
  }, [
    user?.id,
    isAuthenticated,
    user?.companyId,
    selectedCompany?.id,
    availableCompanies,
    chatUIModel,
    createThread,
    selectCompany,
    selectThread,
  ]);

  useEffect(() => {
    if (!activeThreadId || isLoadingMessages || isTyping || messages.length > 0) return;
    if (rcKickoffStarted.current) return;
    const sourceKey = readPersistedLandingSourceKey();
    if (!isRcProspectSourceKey(sourceKey)) return;
    const flag = `krysalis_rc_prospect_kickoff_${activeThreadId}`;
    if (sessionStorage.getItem(flag)) return;
    rcKickoffStarted.current = true;
    sessionStorage.setItem(flag, '1');
    void sendMessage(readRcProspectKickoff());
  }, [activeThreadId, isLoadingMessages, isTyping, messages.length, sendMessage]);

  const handleSend = useCallback(
    async (text: string) => {
      await sendMessage(text);
    },
    [sendMessage]
  );

  const skipToControlBridgeAllowed = isProspectorSkipToControlBridgeAllowed(
    user,
    user?.onboarding
  );

  if (userLoading || !isAuthenticated) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user?.companyId && !selectedCompany?.id) {
    if (!companiesLoaded) {
      return (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" />
          <Paragraph type="secondary" style={{ marginTop: 16 }}>
            Loading your company…
          </Paragraph>
        </div>
      );
    }
    if (availableCompanies.length === 0) {
      return (
        <div style={{ maxWidth: 560, margin: '48px auto', padding: 24 }}>
          <Alert
            type="error"
            showIcon
            message="No companies found"
            description={COMPANY_CREATE_INSTRUCTION}
          />
          <Space style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate('/register')}>
              Create company
            </Button>
            <Button onClick={() => navigate('/dashboard')}>Go to dashboard</Button>
          </Space>
        </div>
      );
    }
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', padding: 24 }}>
        <Alert
          type="warning"
          showIcon
          message="Choose a company to continue"
          description={
            <>
              <Paragraph style={{ marginBottom: 12 }}>{COMPANY_MENU_INSTRUCTION_SHORT}</Paragraph>
              <Space direction="vertical" style={{ width: '100%' }}>
                {availableCompanies.map((userCompany) => (
                  <Button
                    key={userCompany.company.id}
                    block
                    onClick={() => void selectCompany(userCompany.company.id)}
                  >
                    {userCompany.company.name}
                  </Button>
                ))}
              </Space>
            </>
          }
        />
      </div>
    );
  }

  const effectiveCompanyId = selectedCompany?.id || user?.companyId;
  if (
    effectiveCompanyId &&
    availableCompanies.length > 0 &&
    !availableCompanies.some((uc) => uc.company.id === effectiveCompanyId)
  ) {
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', padding: 24 }}>
        <Alert
          type="warning"
          showIcon
          message="Company not linked to this session"
          description="Your profile lists a company that is not in your accessible companies list. Try refreshing or contact support."
        />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/dashboard')}>
          Go to dashboard
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 120px)',
        maxWidth: 920,
        margin: '0 auto',
        padding: '16px 24px 24px',
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Space align="start">
          <RobotOutlined style={{ fontSize: 28, color: '#1677ff' }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {isRcProspectSourceKey(readPersistedLandingSourceKey())
                ? 'Resilience Circle prospect'
                : 'Discovery chat'}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {isRcProspectSourceKey(readPersistedLandingSourceKey())
                ? 'The prospect agent will ask the Resilience Circle ICP questions. Answer in your own words. It matches you to the right assessment and profile.'
                : 'Your prospector agent helps clarify context before you open the Control Bridge. This is not the Bridge — it is the guided conversation from the intake flow.'}
            </Paragraph>
          </div>
        </Space>
      </div>

      {bootstrapError && (
        <Alert type="error" message={bootstrapError} style={{ marginBottom: 12 }} showIcon />
      )}

      <div
        ref={messagesContainerRef}
        style={{
          flex: 1,
          overflow: 'auto',
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          padding: 16,
          background: '#fafafa',
          marginBottom: 12,
        }}
      >
        {isLoadingMessages && messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">Connecting to your agent…</Text>
            </div>
          </div>
        ) : (
          <>
            {messages.map((m) => (
              <div
                key={m.id}
                style={{
                  marginBottom: 12,
                  textAlign: m.who === 'user' ? 'right' : 'left',
                }}
              >
                <div
                  style={{
                    display: 'inline-block',
                    maxWidth: '85%',
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: m.who === 'user' ? '#1677ff' : '#fff',
                    color: m.who === 'user' ? '#fff' : 'rgba(0,0,0,0.88)',
                    border: m.who === 'user' ? undefined : '1px solid #f0f0f0',
                  }}
                >
                  {m.who === 'assistant' ? (
                    <div>
                      {m.metadata?.artifact_payload != null && (
                        <ProspectorArtifactSnapshot payload={m.metadata.artifact_payload} />
                      )}
                      <div className="markdown-content">
                        <ReactMarkdown>{parseMessageContent(m.text)}</ReactMarkdown>
                      </div>
                    </div>
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{m.text}</span>
                  )}
                </div>
              </div>
            ))}
            <AgentReplyWaitIndicator active={isTyping} />
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <ChatInput
        onSendMessage={handleSend}
        placeholder="Reply to your prospector agent…"
        disabled={isTyping}
      />

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Space wrap>
          <Button type="default" onClick={() => navigate('/onboarding/paywall')}>
            Continue to pricing &amp; Foundation Session
          </Button>
          <Button
            type="link"
            disabled={!skipToControlBridgeAllowed}
            onClick={() => navigate(CONTROL_BRIDGE_HOME_PATH)}
          >
            Skip to Control Bridge
          </Button>
        </Space>
        {!skipToControlBridgeAllowed ? (
          <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            Continue Discovery or open pricing before skipping to Control Bridge.
          </Paragraph>
        ) : null}
      </div>
    </div>
  );
};
